/**
 * Delay - Effet de délai avec sync BPM
 * Style PCM-42 / tape delay
 */

import * as Tone from 'tone';

export class Delay {
    constructor(audioEngine) {
        this.audioEngine = audioEngine;

        // Paramètres par défaut
        this.params = {
            time: '8n',         // Subdivision (sync BPM)
            feedback: 0.3,      // 0-0.9
            mix: 0.3,           // Dry/Wet 0-1
            enabled: true
        };

        this.setupAudio();
    }

    setupAudio() {
        // Delay avec feedback
        this.delay = new Tone.FeedbackDelay({
            delayTime: this.params.time,
            feedback: this.params.feedback,
            wet: this.params.mix
        });

        // Filtre passe-bas sur le feedback (son tape delay)
        this.filter = new Tone.Filter({
            frequency: 3000,
            type: 'lowpass'
        });

        // Chaîne : delay -> filtre -> sortie
        this.delay.connect(this.filter);

        // Input/Output nodes
        this.input = this.delay;
        this.output = this.filter;
    }

    /**
     * Connecte une source audio au delay
     */
    connectSource(source) {
        source.connect(this.input);
    }

    /**
     * Connecte la sortie du delay à une destination
     */
    connectTo(destination) {
        this.output.connect(destination);
    }

    /**
     * Déconnecte la sortie
     */
    disconnect() {
        this.output.disconnect();
    }

    // --- Setters ---

    setTime(value) {
        this.params.time = value;
        this.delay.delayTime.value = value;
    }

    setFeedback(value) {
        this.params.feedback = Math.min(0.9, Math.max(0, value)); // Limiter à 0.9 pour éviter l'emballement
        this.delay.feedback.value = this.params.feedback;
    }

    setMix(value) {
        this.params.mix = value;
        this.delay.wet.value = value;
    }

    setEnabled(enabled) {
        this.params.enabled = enabled;
        this.delay.wet.value = enabled ? this.params.mix : 0;
    }

    setFilterFreq(value) {
        this.filter.frequency.value = value;
    }

    /**
     * Active/désactive l'effet (pour compatibilité avec auto-bypass)
     * @param {boolean} active - true pour activer, false pour désactiver
     */
    setActive(active) {
        // Le delay n'a pas de LFO à stopper, on utilise setEnabled
        this.setEnabled(active);
    }

    // --- Getters ---

    get time() { return this.params.time; }
    get feedback() { return this.params.feedback; }
    get mix() { return this.params.mix; }
    get enabled() { return this.params.enabled; }

    /**
     * Retourne tous les paramètres (pour sauvegarde)
     */
    getParams() {
        return { ...this.params };
    }

    /**
     * Charge des paramètres (depuis sauvegarde)
     */
    setParams(params) {
        if (params.time) this.setTime(params.time);
        if (params.feedback !== undefined) this.setFeedback(params.feedback);
        if (params.mix !== undefined) this.setMix(params.mix);
        if (params.enabled !== undefined) this.setEnabled(params.enabled);
    }
}
